import { useState } from 'react'
import { Box, Typography } from '@mui/material'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import AssignmentReturnOutlinedIcon from '@mui/icons-material/AssignmentReturnOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import SentimentDissatisfiedOutlinedIcon from '@mui/icons-material/SentimentDissatisfiedOutlined'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined'
import FlipOutlinedIcon from '@mui/icons-material/FlipOutlined'
import palette from '../../../theme/palette'
import { C, Card, CountUp, IconBubble, InfoTip, Pill, Reveal, SectionHead, Source, textOn, useInView } from './aboutUi'
import { NextBanner } from './WhatTab'

// ─────────────────────────────────────────────────────────────────────────────
// 100-dot waffle: out of 100 orders, how many deliver vs come back.
// Numbers: Prozo network forward shipments, 6-month window (3.52M rows).
// ─────────────────────────────────────────────────────────────────────────────
const OUTCOMES = {
  cod: { label: 'COD orders', delivered: 65.1, rto: 28.5 },
  prepaid: { label: 'Prepaid orders', delivered: 87.7, rto: 7.4 },
}

function Waffle() {
  const [mode, setMode] = useState('cod')
  const [ref, seen] = useInView(0.3)
  const o = OUTCOMES[mode]
  const del = Math.round(o.delivered)
  const rto = Math.round(o.rto)
  return (
    <Card hover={false} sx={{ p: { xs: 2.5, md: 4 } }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '1fr 1.1fr' }, gap: 4, alignItems: 'center' }}>
        <Box ref={ref} sx={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: { xs: 0.6, md: 0.9 }, maxWidth: 380, mx: 'auto', width: '100%' }}>
          {Array.from({ length: 100 }).map((_, i) => {
            const color = i < del ? C.success : i < del + rto ? C.error : palette.grey[400]
            return (
              <Box key={i} sx={{
                aspectRatio: '1', borderRadius: '28%', background: seen ? color : C.line,
                transform: seen ? 'scale(1)' : 'scale(.4)', transition: `background .4s ease ${i * 12}ms, transform .4s ease ${i * 12}ms`,
              }} />
            )
          })}
        </Box>
        <Box>
          <Box sx={{ display: 'inline-flex', p: 0.5, borderRadius: 99, background: C.soft, border: `1px solid ${C.line}`, mb: 2.5 }}>
            {Object.entries(OUTCOMES).map(([k, v]) => (
              <Box key={k} onClick={() => setMode(k)} sx={{
                cursor: 'pointer', px: 2, py: 0.75, borderRadius: 99, fontWeight: 700, fontSize: 14, transition: 'all .2s',
                color: mode === k ? '#fff' : C.muted, background: mode === k ? C.primary : 'transparent',
              }}>{v.label}</Box>
            ))}
          </Box>
          <Typography sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 700, color: C.ink, lineHeight: 1.2 }}>
            Out of every 100 {mode === 'cod' ? 'COD' : 'prepaid'} orders,{' '}
            <Box component='span' sx={{ color: C.error }}>{rto} come back</Box>.
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, mt: 2.5, flexWrap: 'wrap' }}>
            <Box>
              <Typography key={`d${mode}`} sx={{ fontSize: 36, fontWeight: 700, color: C.success }}><CountUp to={o.delivered} decimals={1} suffix='%' /></Typography>
              <Typography sx={{ color: C.muted, fontSize: 13 }}>delivered</Typography>
            </Box>
            <Box>
              <Typography key={`r${mode}`} sx={{ fontSize: 36, fontWeight: 700, color: C.error }}><CountUp to={o.rto} decimals={1} suffix='%' /></Typography>
              <Typography sx={{ color: C.muted, fontSize: 13 }}>returned to origin (RTO)</Typography>
            </Box>
          </Box>
          <Typography sx={{ color: C.ink, mt: 2, fontSize: 15 }}>
            A COD order is about <b>4× more likely</b> to come back than a prepaid one. Much of that gap depends on which courier carries it. Some couriers are far better at converting a hesitant COD buyer on the doorstep.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 2, fontSize: 12.5, color: C.muted, flexWrap: 'wrap' }}>
            <span><b style={{ color: C.success }}>■</b> Delivered</span><span><b style={{ color: C.error }}>■</b> RTO</span><span><b style={{ color: palette.grey[400] }}>■</b> Other (lost, cancelled, in transit)</span>
          </Box>
          <Source>Prozo network forward shipments, 6-month window, 3.5M+ orders</Source>
        </Box>
      </Box>
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// What one RTO costs: a cascade of costs (qualitative, no made-up rupees).
// ─────────────────────────────────────────────────────────────────────────────
const RTO_COSTS = [
  { t: 'Forward freight', d: 'Already paid, and wasted', icon: <LocalShippingOutlinedIcon />, c: C.orange },
  { t: 'Return freight', d: 'Paid again to bring it back', icon: <AssignmentReturnOutlinedIcon />, c: C.error },
  { t: 'Stuck inventory', d: 'Days away from sellable stock, plus repacking and QC', icon: <Inventory2OutlinedIcon />, c: C.warning },
  { t: 'Lost sale & ad spend', d: 'The money spent to acquire that buyer is gone', icon: <CampaignOutlinedIcon />, c: C.violet },
  { t: 'Unhappy customer', d: 'Failed attempts and late orders hurt repeat purchase', icon: <SentimentDissatisfiedOutlinedIcon />, c: C.info },
  { t: 'Ops firefighting', d: 'NDR calls, escalations, courier follow-ups', icon: <SupportAgentOutlinedIcon />, c: C.teal },
]

// ─────────────────────────────────────────────────────────────────────────────
// "Best courier depends on the lane" heatmap (illustrative).
// ─────────────────────────────────────────────────────────────────────────────
const LANES = ['Metro → Metro', 'Metro → Tier-2', 'Metro → North-East', 'Tier-2 → Rural']
const HEAT = [
  { name: 'Courier X', v: [96, 88, 71, 64] },
  { name: 'Courier Y', v: [93, 91, 84, 70] },
  { name: 'Courier Z', v: [90, 82, 76, 83] },
]
// Low → high delivery success runs error → warning → success, using the theme's own colours.
const HEAT_STOPS = [palette.error.main, palette.warning.main, palette.success.main].map((h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)))
const heatColor = (v) => {
  const t = Math.max(0, Math.min(1, (v - 60) / 38)) * (HEAT_STOPS.length - 1)
  const i = Math.min(HEAT_STOPS.length - 2, Math.floor(t))
  const [a, b] = [HEAT_STOPS[i], HEAT_STOPS[i + 1]]
  const rgb = a.map((x, k) => Math.round(x + (b[k] - x) * (t - i)))
  const lum = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255
  return { bg: `rgb(${rgb.join(',')})`, fg: lum > 0.62 ? C.ink : '#fff' }
}

function LaneHeatmap() {
  const [ref, seen] = useInView(0.3)
  return (
    <Box ref={ref} sx={{ overflowX: 'auto' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '110px repeat(4, minmax(88px, 1fr))', gap: 1, minWidth: 480 }}>
        <Box />
        {LANES.map((l) => <Typography key={l} sx={{ fontSize: 12, fontWeight: 700, color: C.muted, textAlign: 'center' }}>{l}</Typography>)}
        {HEAT.map((row, ri) => (
          <Box key={row.name} sx={{ display: 'contents' }}>
            <Typography sx={{ fontWeight: 700, color: C.ink, alignSelf: 'center', fontSize: 14 }}>{row.name}</Typography>
            {row.v.map((v, ci) => {
              const best = Math.max(...HEAT.map((r) => r.v[ci])) === v
              const heat = heatColor(v)
              return (
                <Box key={ci} sx={{
                  height: 54, borderRadius: 1.5, display: 'grid', placeItems: 'center', color: heat.fg, fontWeight: 700, fontSize: 17, position: 'relative',
                  background: seen ? heat.bg : C.line, transition: `background .5s ease ${(ri * 4 + ci) * 60}ms`,
                  outline: best ? `3px solid ${C.ink}` : 'none', outlineOffset: 2,
                }}>
                  {seen ? `${v}%` : ''}
                  {best && seen && <Box sx={{ position: 'absolute', top: -9, right: -6, background: C.ink, color: '#fff', fontSize: 9.5, px: 0.75, py: 0.2, borderRadius: 1 }}>BEST</Box>}
                </Box>
              )
            })}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Old way vs PACE: click-to-flip cards.
// ─────────────────────────────────────────────────────────────────────────────
const FLIPS = [
  { t: 'Thin data', old: 'Most courier-route pairs have fewer than 10 past shipments. A rule engine will happily rank a courier on 1 lucky delivery.',
    pace: 'PACE discounts small samples and borrows evidence from similar lanes, the drop area and the state, so thin data never looks like proof.' },
  { t: 'Old news', old: 'Last quarter\'s average counts as much as last week\'s. A courier that just went bad keeps getting orders.',
    pace: 'Recent weeks count more, and track records refresh every night. A courier\'s bad week shows up in the ranking by the next morning.' },
  { t: 'COD blind spot', old: 'COD is only a yes/no filter. Rules don\'t know which courier converts COD orders in which area.',
    pace: 'PACE predicts RTO risk for every order and courier, using the payment mode, order value and the buyer area\'s history.' },
  { t: 'List-price trap', old: 'Picks the cheapest rate on paper, then the weight-dispute bills arrive a month later.',
    pace: 'Compares true cost: the rate card plus the surcharge each courier is statistically likely to add later.' },
  { t: 'One answer, no reason', old: 'Returns one courier. When ops ask "why this one?", nobody can answer.',
    pace: 'Ranks every courier and shows the contribution of delivery, first attempt, on-time and cost behind each decision.' },
  { t: 'Blind to disruptions', old: 'Floods, strikes and festival backlogs are only noticed after orders get stuck.',
    pace: 'Logged disruptions shift the affected couriers\' ETA at allocation time, so orders flow around the problem.' },
]

function FlipCard({ f, i }) {
  const [flipped, setFlipped] = useState(false)
  return (
    <Box onClick={() => setFlipped((x) => !x)} sx={{ perspective: 1200, cursor: 'pointer', height: 230 }}>
      <Box sx={{ position: 'relative', width: '100%', height: '100%', transition: 'transform .7s cubic-bezier(.3,.8,.3,1)', transformStyle: 'preserve-3d', transform: flipped ? 'rotateY(180deg)' : 'none' }}>
        {[false, true].map((back) => (
          <Box key={String(back)} sx={{
            position: 'absolute', inset: 0, backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', borderRadius: 2, p: 2.5,
            transform: back ? 'rotateY(180deg)' : 'none', display: 'flex', flexDirection: 'column',
            background: back ? C.primary : '#fff', color: back ? '#fff' : C.ink,
            border: back ? 'none' : `1px solid ${palette.divider}`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Pill color={back ? '#fff' : C.warning} solid={false}>{back ? 'With PACE' : `Problem ${i + 1}`}</Pill>
              <FlipOutlinedIcon sx={{ fontSize: 18, opacity: 0.5 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: 19, mt: 1.5, color: back ? '#fff' : C.ink }}>{f.t}</Typography>
            <Typography sx={{ mt: 1, fontSize: 14.5, opacity: back ? 0.95 : 0.8, flex: 1 }}>{back ? f.pace : f.old}</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 700, opacity: 0.6 }}>{back ? '← Tap to see the problem' : 'Tap to see how PACE fixes it →'}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default function WhyTab({ onNext }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 7 }}>
      <Box>
        <Reveal>
          <SectionHead kicker='The problem' color={C.warning} title='Courier choice quietly decides whether you make money on an order'
            sub='The courier you pick affects whether the order is delivered, delivered on time, or sent back at your cost. On COD, the stakes are huge.' />
        </Reveal>
        <Reveal delay={100}><Waffle /></Reveal>
      </Box>

      <Box>
        <Reveal>
          <SectionHead kicker='The hidden bill' color={C.orange} title='Every RTO is paid for six times over'
            sub='The return freight is only the visible part. Here is everything a returned order really costs.' />
        </Reveal>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(6, 1fr)' }, gap: 2 }}>
          {RTO_COSTS.map((c, i) => (
            <Reveal key={c.t} delay={i * 90}>
              <Card sx={{ textAlign: 'center', height: '100%', p: 2.5 }} accent={c.c}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}><IconBubble icon={c.icon} color={c.c} size={46} /></Box>
                <Typography sx={{ fontWeight: 700, color: C.ink, mt: 1.5, fontSize: 15 }}>{c.t}</Typography>
                <Typography sx={{ fontSize: 13, color: C.muted, mt: 0.5 }}>{c.d}</Typography>
              </Card>
            </Reveal>
          ))}
        </Box>
        <Reveal delay={200}>
          <Box sx={{ mt: 2.5, p: 2.5, borderRadius: 1.5, background: `${C.warning}1A`, border: `1px solid ${C.warning}66`, textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 700, color: C.ink, fontSize: 17 }}>
              Preventing one RTO often saves more than shaving ₹10 off freight on a hundred orders.
            </Typography>
            <Typography sx={{ color: C.muted, fontSize: 14, mt: 0.5 }}>That is why PACE optimises for outcomes, not just the lowest rate.</Typography>
          </Box>
        </Reveal>
      </Box>

      <Box>
        <Reveal>
          <SectionHead kicker='The insight' color={C.violet} title='There is no single "best courier". It changes by lane, and week by week.' />
        </Reveal>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '1fr 1.4fr' }, gap: 3 }}>
          <Reveal>
            <Card hover={false} sx={{ height: '100%' }}>
              <Typography sx={{ fontWeight: 700, color: C.ink, display: 'flex', alignItems: 'center' }}>
                Same courier brand, very different results
                <InfoTip title='Why does one courier vary so much?' color={C.violet}>
                  <p>A courier&apos;s performance depends on the hub network, the last-mile partner in each area and the account setup. On Prozo&apos;s network, delivery rates for accounts of one national courier brand range from about 61% to 96%.</p>
                </InfoTip>
              </Typography>
              <Box sx={{ mt: 3, position: 'relative', height: 60 }}>
                <Box sx={{ position: 'absolute', top: 24, left: 0, right: 0, height: 12, borderRadius: 6, background: `linear-gradient(90deg, ${C.error}, ${C.warning}, ${C.success})` }} />
                {[{ v: 61, l: '0%' }, { v: 96, l: '100%' }].map((m) => (
                  <Box key={m.v} sx={{ position: 'absolute', left: m.l, top: 0, transform: 'translateX(-50%)', textAlign: 'center' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 22, color: C.ink, lineHeight: 1 }}>{m.v}%</Typography>
                    <Box sx={{ width: 4, height: 32, background: C.ink, mx: 'auto', borderRadius: 2 }} />
                  </Box>
                ))}
              </Box>
              <Typography sx={{ color: C.muted, fontSize: 14, mt: 3 }}>
                Delivery rate across accounts of <b>one</b> national courier on Prozo&apos;s network. A rule like &quot;always use courier X&quot; leaves this spread on the table.
              </Typography>
              <Source>Prozo network, 6-month window</Source>
            </Card>
          </Reveal>
          <Reveal delay={120}>
            <Card hover={false} sx={{ height: '100%' }}>
              <Typography sx={{ fontWeight: 700, color: C.ink, mb: 2 }}>The winner changes from lane to lane</Typography>
              <LaneHeatmap />
              <Typography sx={{ color: C.muted, fontSize: 13.5, mt: 2 }}>
                Delivery success by lane (illustrative). The best courier is different in every column. PACE makes this call separately for every order, and refreshes it every night.
              </Typography>
            </Card>
          </Reveal>
        </Box>
      </Box>

      <Box>
        <Reveal>
          <SectionHead kicker='Why rules fall short' color={C.primary} title='Rule engines vs PACE: tap each card to flip it'
            sub='Most allocation today runs on fixed rules and historical averages. Here is where they break, and what PACE does instead.' />
        </Reveal>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: 2.5 }}>
          {FLIPS.map((f, i) => <Reveal key={f.t} delay={(i % 3) * 100}><FlipCard f={f} i={i} /></Reveal>)}
        </Box>
      </Box>

      <Box>
        <Reveal>
          <SectionHead kicker='Why Prozo' color={C.teal} title='AI is only as good as its data, and this data runs through Prozo every day' align='center' />
        </Reveal>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2.5 }}>
          {[
            { v: 14, s: 'M+', t: 'orders of shipment history', c: C.primary },
            { v: 400, s: 'K+', t: 'new orders learned from every month', c: C.violet },
            { v: 22000, s: '+', t: 'pincodes covered', c: C.info },
            { v: 130, s: '+', t: 'actively shipping couriers compared', c: C.success },
          ].map((x, i) => (
            <Reveal key={x.t} delay={i * 100}>
              <Card sx={{ textAlign: 'center', height: '100%' }} accent={x.c}>
                <Typography sx={{ fontSize: { xs: 30, md: 40 }, fontWeight: 700, color: textOn(x.c) }}><CountUp to={x.v} suffix={x.s} /></Typography>
                <Typography sx={{ color: C.muted, fontSize: 14 }}>{x.t}</Typography>
              </Card>
            </Reveal>
          ))}
        </Box>
        <Reveal delay={200}>
          <Typography sx={{ textAlign: 'center', color: C.muted, mt: 2, fontSize: 14 }}>
            Every delivery, failed attempt, NDR call, RTO and weight dispute across many brands. A single brand could never see this much.
          </Typography>
        </Reveal>
      </Box>

      <NextBanner label='Next: The impact on your business' color={C.success} onClick={onNext} />
    </Box>
  )
}
